import { Link, useNavigate, useSearchParams } from 'react-router'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { TablePagination } from '@/components/ui/pagination'
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SkeletonRows } from '@/components/ui/skeleton'
import { formatDate, formatRelativeDate } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import {
  type SalesLeadListItem,
  SalesLeadStatus,
  salesLeadStatusColors,
  salesLeadStatusLabels,
  useSalesLeads,
} from '@/models/salesLead'
import { usePageParam } from './usePageParam'

const ALL_STATUSES = 'all'

const COLUMNS: ResponsiveTableColumn<SalesLeadListItem>[] = [
  {
    key: 'company',
    header: 'Company',
    cellClassName: 'font-medium',
    hideOnCard: true,
    cell: (lead) => lead.legalName,
  },
  {
    key: 'status',
    header: 'Status',
    cell: (lead) => (
      <Badge className={cn(salesLeadStatusColors[lead.status], 'text-xs')}>
        {salesLeadStatusLabels[lead.status]}
      </Badge>
    ),
  },
  { key: 'units', header: 'Units', cell: (lead) => lead.powerUnits },
  { key: 'expires', header: 'Expires', cell: (lead) => formatDate(lead.policyExpirationDate) },
  {
    key: 'lastActivity',
    header: 'Last activity',
    cell: (lead) => formatRelativeDate(lead.lastActivityAt, 'No activity'),
  },
]

export function StaffLeadsTab({ staffUserId }: { staffUserId: string }) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = usePageParam()
  const status = searchParams.get('status') ?? ''
  const { leads, count, hasNextPage, hasPreviousPage, isLoading } = useSalesLeads({
    page,
    assignedRep: staffUserId,
    status: status || undefined,
    ordering: '-last_activity_at',
  })

  function setStatus(value: string) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (value === ALL_STATUSES) params.delete('status')
        else params.set('status', value)
        params.delete('page')
        return params
      },
      { preventScrollReset: true }
    )
  }

  const fullList = new URLSearchParams({ rep: staffUserId })
  if (status) fullList.set('status', status)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Select value={status || ALL_STATUSES} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-56">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>All statuses</SelectItem>
            {Object.values(SalesLeadStatus).map((value) => (
              <SelectItem key={value} value={value}>
                {salesLeadStatusLabels[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="link" asChild className="h-auto p-0 text-sm">
          <Link to={`/insure/internal/sales-leads?${fullList.toString()}`}>Open in Sales leads</Link>
        </Button>
      </div>
      {isLoading ? (
        <SkeletonRows rows={5} />
      ) : (
        <ResponsiveTable
          items={leads}
          columns={COLUMNS}
          getRowId={(lead) => lead.id}
          cardTitle={(lead) => lead.legalName}
          onRowClick={(lead) => navigate(`/insure/internal/sales-leads/${lead.id}`)}
          empty="No leads assigned"
        />
      )}
      <TablePagination
        page={page}
        count={count}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={setPage}
        itemLabel="lead"
      />
    </div>
  )
}
